import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGDPRConsent } from '../hooks/useGDPRConsent';
import { supabase } from '../lib/supabase';

export default function ConsentGuard() {
  const { hasConsent, isInitialized } = useGDPRConsent();
  const location = useLocation();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  
  // Check authentication status
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        setIsAuthenticated(!!data.session);
      } catch (error) {
        console.error('Error checking auth:', error);
      } finally {
        setCheckingAuth(false);
      }
    };
    
    checkAuth();
    
    // Subscribe to auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsAuthenticated(!!session);
    });
    
    return () => {
      subscription.unsubscribe();
    };
  }, []);
  
  useEffect(() => {
    // Don't do anything until both consent and auth checks are complete
    if (!isInitialized || checkingAuth) return;
    
    // Allow access to these paths without consent
    const allowedPaths = [
      '/', 
      '/login', 
      '/register', 
      '/despre',
      '/payment-success', 
      '/payment-cancel'
    ];
    
    // Check if current path is allowed without consent
    const isAllowedPath = allowedPaths.some(path => 
      location.pathname === path || 
      (path !== '/' && location.pathname.startsWith(path + '/'))
    );
    
    // If user is authenticated, allow them to access their pages
    if (isAuthenticated) {
      return;
    }
    
    // If user hasn't given consent and tries to navigate to a restricted path,
    // redirect them back to the home page
    if (!hasConsent && !isAllowedPath) {
      navigate('/', { replace: true });
    }
  }, [hasConsent, isInitialized, checkingAuth, isAuthenticated, location.pathname, navigate]);

  // This is a background component with no UI
  return null;
}